import { Inset } from '@doordash/design-language'
import * as React from 'react'
import * as DA from '../../../typeModules/events'
import { Modal } from '@doordash/component-modal'
import Separator from '@doordash/component-separator'
import NavBar from '@doordash/component-navbar'
import Button from '@doordash/component-button'
import { SlideButtonContainer, ProgressContainer } from './styles'
import { i18nKeys } from 'translationTypes'
import i18n from '../../../i18n'
import { getSlideDeck } from './factory'
import { SlideProps } from './factory/types'
import { ErrorScreen } from './ErrorScreen'
import { StepIndicator } from './StepIndicator'

export const TrainingCourse: React.FC<SlideProps> = ({
  onClose,
  onSubmit,
  onAnimationEnd,
  trackTrainingCourse,
  buttonState,
  courseId,
  currentSlide,
  country,
  isError,
  dxTrainingCourse,
}) => {
  const slideDeck = getSlideDeck(country, {
    courseId,
    buttonState,
    trackTrainingCourse,
    onSubmit,
    onAnimationEnd,
  })

  const [node, setNode] = React.useState()

  const myRef = React.useCallback(node => {
    if (node) {
      setNode(node)
    }
  }, [])

  const executeScroll = () =>
    setTimeout(() => {
      ;(node as HTMLDivElement).scrollIntoView(true)
    })

  const [slide, setSlide] = React.useState(currentSlide)

  const modalProps = {
    title: i18n.t(i18nKeys.safetyCourse__title),
    hasContentPadding: false,
    onClose: () => {
      trackTrainingCourse(
        DA.EventActions.Close,
        DA.SafetyCourseSections.Course,
        courseId,
        { slide }
      )
      onClose(slide)
    },
    hasCloseButton: false,
    headerImage: false,
    headerAlignment: Modal.HeaderAlignment.Center,
    shouldRenderStickyHeader: false,
    renderContentHeader: ({ title }) => (
      <NavBar
        onNavigationClick={() => {
          trackTrainingCourse(
            DA.EventActions.Close,
            DA.SafetyCourseSections.Course,
            courseId,
            { slide }
          )
          onClose(slide)
        }}
        textAlign={NavBar.TextAlignments.Center}
        isSeparatorVisible={false}
        headerImage={false}
        navigationType={NavBar.NavigationTypes.Close}
        title={title}
      />
    ),
  }

  let lastSlide
  let step

  if (slideDeck) {
    lastSlide = slideDeck.length - 1
    step = slideDeck[slide]
  }

  const shouldDisplayError = isError || !step
  const dataTrack = shouldDisplayError
    ? {
        error: dxTrainingCourse.error,
        slides: slideDeck ? slideDeck.length : 0,
      }
    : {}

  trackTrainingCourse(
    DA.EventActions.Rendered,
    DA.SafetyCourseSections.Course,
    courseId,
    dataTrack
  )

  if (shouldDisplayError) {
    return (
      <Modal {...modalProps}>
        <ErrorScreen />
        <Inset />
      </Modal>
    )
  }

  return (
    <Modal {...modalProps}>
      <div ref={myRef}>
        <Separator type={Separator.Types.FullBorder} />
        <ProgressContainer>
          <StepIndicator totalStep={slideDeck.length} currentStep={slide + 1} />
        </ProgressContainer>
        <Separator type={Separator.Types.FullBorder} />
      </div>

      {step}

      <Inset>
        <SlideButtonContainer>
          {slide !== 0 && slide !== lastSlide && (
            <Button
              type={Button.Types.Tertiary}
              onClick={() => {
                trackTrainingCourse(
                  DA.EventActions.Previous,
                  DA.SafetyCourseSections.Course,
                  courseId,
                  { slide }
                )
                setSlide(slide - 1)
                executeScroll()
              }}
            >
              {i18n.t(i18nKeys.common__previous)}
            </Button>
          )}

          {slide > 0 && slide < lastSlide && (
            <Inset horizontalSize={Inset.Sizes.XxxSmall} />
          )}

          {slide !== lastSlide && (
            <Button
              type={Button.Types.PrimaryPill}
              onClick={() => {
                trackTrainingCourse(
                  DA.EventActions.Next,
                  DA.SafetyCourseSections.Course,
                  courseId,
                  { slide }
                )
                setSlide(slide + 1)
                executeScroll()
              }}
            >
              {i18n.t(i18nKeys.common__next)}
            </Button>
          )}
        </SlideButtonContainer>
      </Inset>
    </Modal>
  )
}

export default TrainingCourse
